import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import {
  CustomAppDialog,
  CustomDialogCloseButton,
} from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import { deleteGhatReceiptApprovalItem } from "./services/gharteceipt.services";
import {
  closeApprovalItemDeleteConfirmDialog,
  openApprovalItemDeleteConfirmDialog,
  setGhatReceiptApprovalItemIndex,
} from "./ghatReceiptSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IItem } from "../item/itemModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { APPROVAL_TYPE } from "../../_contstants/common";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import { findAllActiveItem } from "../item/services/item.services";

const GhatReceiptApprovalItemsArray = (
  fieldArrayRenderProps: FieldArrayRenderProps,
) => {
  const dispatch = useAppDispatch();
  const GhatReceiptApprovalItemIndex = useAppSelector(
    (state) => state.ghatreceipt.GhatReceiptApprovalItemIndex,
  );
  const ApprovalItemDeleteConfirmDialog = useAppSelector(
    (state) => state.ghatreceipt.ApprovalItemDeleteConfirmDialog,
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const isViewDialog = dialogName.startsWith("view-approval-items-");
  const fieldName = fieldArrayRenderProps.name;
  const ItemList = useAppSelector((state) => state.item.ItemList);
  const UnitList = useAppSelector((state) => state.unit.UnitList);
  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index].id) {
        const response = await dispatch(
          deleteGhatReceiptApprovalItem(fieldArrayRenderProps?.value[index].id),
        );
        if (response.meta.requestStatus === "fulfilled") {
          fieldArrayRenderProps.onRemove({ index: index });
          dispatch(closeApprovalItemDeleteConfirmDialog());
          dispatch(setGhatReceiptApprovalItemIndex(-1));
        }
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeApprovalItemDeleteConfirmDialog());
        dispatch(setGhatReceiptApprovalItemIndex(-1));
      }
    },
    [fieldArrayRenderProps],
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        type: "",
        item_id: null,
        weight: null,
        qty: null,
        unit_id: null,
        used_weight: null,
        return_weight: null,
        balance_weight: null,
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openApprovalItemDeleteConfirmDialog());
    dispatch(setGhatReceiptApprovalItemIndex(index));
  };

  return (
    <>
      <CustomAppDialog open={ApprovalItemDeleteConfirmDialog}>
        <>
          <Typography.h5>{"Delete Approval Items Item"}</Typography.h5>
          <GridLayout>
            <GridLayoutItem>
              <Typography.p>Are you sure you want to delete this?</Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <CustomDialogCloseButton
                themeColor="error"
                closeDialogFunction={closeApprovalItemDeleteConfirmDialog}
              />
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() => handleDeleteAction(GhatReceiptApprovalItemIndex)}
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </>
      </CustomAppDialog>
      <GridLayout cols={[{ width: "50%" }, { width: "50%" }]}>
        <GridLayoutItem>
          <Typography.h5 style={{ marginBottom: 5 }}>
            Approval Items
          </Typography.h5>
        </GridLayoutItem>
        <GridLayoutItem style={{ textAlign: "left", marginLeft: "auto" }}>
          {!isViewDialog && (
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
            >
              Add Item
            </Button>
          )}
        </GridLayoutItem>
      </GridLayout>
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <div
            className="table-responsive"
            style={{
              maxHeight: isViewDialog ? "50vh" : "20vh",
              scrollbarWidth: "thin",
              marginTop: 10,
            }}
          >
            <table
              className="table table-striped table-bordered table-hover field-array-table"
              style={{ minWidth: 2000 }}
            >
              <thead>
                <tr>
                  <th style={{ width: "8%" }}>Type</th>
                  <th style={{ width: "12%" }}>Product Name</th>
                  <th style={{ width: "10%" }}>Weight (gm)</th>
                  <th style={{ width: "6%" }}>Quantity</th>
                  <th style={{ width: "8%" }}>Unit</th>
                  <th style={{ width: "10%" }}>Used Weight (gm)</th>
                  <th style={{ width: "10%" }}>Return Weight (gm) </th>
                  <th style={{ width: "12%" }}>Balance Weight (gm)</th>
                  <th style={{ width: "8%" }}>Customer Rate</th>
                  <th style={{ width: "13%" }}>Customer Less Weight (gm)</th>
                  <th
                    style={{
                      width: "10%",
                      position: "sticky",
                      right: 0,
                      zIndex: 10,
                    }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {fieldArrayRenderProps.value &&
                  fieldArrayRenderProps.value.length > 0 &&
                  fieldArrayRenderProps.value.map(
                    (approval_items: any, index: number) => (
                      <tr>
                        <td>
                          <Field
                            name={`${fieldName}.${index}.type`}
                            wrapperStyle={{ margin: 0 }}
                            placeholder="Select Type"
                            disabled={isViewDialog}
                            component={FormSelectionField}
                            options={APPROVAL_TYPE?.map((type: any) => {
                              return {
                                value: type.value,
                                label: type.label,
                              };
                            })}
                            validator={requiredValidator}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.item_id`}
                            isAddNew={true}
                            addNewLink="item/create"
                            disabled={isViewDialog}
                            dispatcherFunction={findAllActiveItem}
                            placeholder="Select Item"
                            component={FormSelectionField}
                            validator={requiredValidator}
                            options={ItemList?.map((item: IItem) => {
                              return {
                                value: item.id,
                                label: item.item_name,
                              };
                            })}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.weight`}
                            placeholder="0.000"
                            disabled={isViewDialog}
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.qty`}
                            placeholder="0.00"
                            disabled={isViewDialog}
                            type="number"
                            format="n2"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.unit_id`}
                            placeholder="Select Unit"
                            disabled={isViewDialog}
                            component={FormSelectionField}
                            validator={requiredValidator}
                            options={UnitList?.map((unit: any) => {
                              return {
                                value: unit.id,
                                label: unit.unit_name,
                              };
                            })}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.used_weight`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            disabled={
                              fieldArrayRenderProps.formRenderProps.valueGetter(
                                `${fieldName}.${index}.type`,
                              ) === "SAMPLE"
                                ? true
                                : false
                            }
                            component={FormNumericTextField}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.return_weight`}
                            placeholder="0.00"
                            // disabled={
                            //   fieldArrayRenderProps.formRenderProps.valueGetter(
                            //     `${fieldName}.${index}.type`,
                            //   ) === "SAMPLE"
                            //     ? true
                            //     : false
                            // }
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.balance_weight`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            disabled={
                              fieldArrayRenderProps.formRenderProps.valueGetter(
                                `${fieldName}.${index}.type`,
                              ) === "SAMPLE"
                                ? true
                                : false
                            }
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.custom_rate`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            disabled={
                              fieldArrayRenderProps.formRenderProps.valueGetter(
                                `${fieldName}.${index}.type`,
                              ) === "SAMPLE"
                                ? true
                                : false
                            }
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.customer_less_weight`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                            disabled={
                              fieldArrayRenderProps.formRenderProps.valueGetter(
                                `${fieldName}.${index}.type`,
                              ) === "SAMPLE"
                                ? true
                                : false
                            }
                          />
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                            backgroundColor: "#f0f0f0",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <IconButton
                            type="button"
                            fillMode="solid"
                            size="small"
                            title="Delete"
                            themeColor="error"
                            onClick={() => handleOpenDeleteDialog(index)}
                            style={{
                              height: 32,
                              width: 32,
                            }}
                          >
                            <MdDelete
                              className="absolute-position"
                              style={{ fontSize: "22px" }}
                            />
                          </IconButton>
                        </td>
                      </tr>
                    ),
                  )}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
};

export default GhatReceiptApprovalItemsArray;
